import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarDays, FileText, RefreshCw, Sparkles, Tags } from 'lucide-react';
import {
  analyzeBannerPromptWeekly,
  BannerPromptWeeklyReport,
  fetchBannerPromptReports,
  NgImprovementCandidate,
  NeededStyleCandidate,
  PromptSignal,
} from '../../services/api/adminBannerPromptReportService';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskySpinner,
  TaskyStatCard,
} from '../tasky';

const formatDate = (value: string) => new Date(value).toLocaleDateString('ja-JP');

const formatRate = (signal: PromptSignal) => {
  if (signal.rate === undefined) return `${signal.count}件`;
  return `${signal.count}件 / ${Math.round(signal.rate * 100)}%`;
};

const signalLabel = (signal: PromptSignal) => signal.label ?? signal.term ?? signal.key ?? '-';

const SignalList: React.FC<{ title: string; items: PromptSignal[]; icon: React.ReactNode }> = ({ title, items, icon }) => (
  <section className="rounded-tasky-md border border-warm-border bg-white p-4">
    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-dark">
      <span className="text-primary [&_svg]:size-4">{icon}</span>
      {title}
    </div>
    {items.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <TaskyBadge key={signalLabel(item)} variant="warm">
            {signalLabel(item)} {formatRate(item)}
          </TaskyBadge>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500">該当データなし</p>
    )}
  </section>
);

const NeededStyleList: React.FC<{ items: NeededStyleCandidate[] }> = ({ items }) => (
  <section className="rounded-tasky-md border border-warm-border bg-white p-4">
    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-dark">
      <Sparkles className="size-4 text-primary" />
      必要なスタイル候補
    </div>
    {items.length > 0 ? (
      <div className="space-y-3">
        {items.map((item) => (
          <div key={`${item.name}-${item.key ?? item.reason}`} className="border-b border-warm-border pb-3 last:border-0 last:pb-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-dark">{item.name}</span>
              <TaskyBadge variant={item.current_style_count === 0 ? 'warning' : 'secondary'}>
                既存{item.current_style_count}件
              </TaskyBadge>
              <span className="text-xs text-gray-500">{item.prompt_count}件</span>
            </div>
            <p className="mt-1 text-sm text-gray-600">{item.reason}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500">該当データなし</p>
    )}
  </section>
);

const NgImprovementList: React.FC<{ items: NgImprovementCandidate[] }> = ({ items }) => (
  <section className="rounded-tasky-md border border-warm-border bg-white p-4">
    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-dark">
      <AlertTriangle className="size-4 text-warning" />
      NG・改善候補
    </div>
    {items.length > 0 ? (
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.label} className="rounded-tasky-sm bg-warm-bg px-3 py-2">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm text-dark">{item.label}</span>
              <span className="text-xs text-gray-500">{item.count}件 / {item.severity}</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">{item.recommendation}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500">該当データなし</p>
    )}
  </section>
);

const BannerPromptWeeklyReports: React.FC = () => {
  const [reports, setReports] = useState<BannerPromptWeeklyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const latestReport = reports[0] ?? null;

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchBannerPromptReports({ source: 'banner_ads', per_page: 8 });
      setReports(result.data);
    } catch (err) {
      console.error(err);
      setError('バナープロンプト週次分析を取得できませんでした。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError(null);
    try {
      await analyzeBannerPromptWeekly({ source: 'banner_ads' });
      await loadReports();
    } catch (err) {
      console.error(err);
      setError('週次分析の実行に失敗しました。');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10">
        <TaskySpinner size="md" />
        <span className="text-sm text-gray-600">データを読み込み中...</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-dark">バナープロンプト週次分析</h2>
          {latestReport && (
            <p className="mt-1 text-sm text-gray-500">
              {formatDate(latestReport.period_start)} - {formatDate(latestReport.period_end)}
            </p>
          )}
        </div>
        <TaskyButton size="sm" onClick={handleAnalyze} disabled={analyzing}>
          <RefreshCw className={analyzing ? 'animate-spin' : undefined} />
          最新週を分析
        </TaskyButton>
      </div>

      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      {!latestReport ? (
        <TaskyEmptyState icon={<FileText />} title="週次分析レポートがありません。" className="my-8" />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <TaskyStatCard label="対象プロンプト" value={`${latestReport.source_rows_count}件`} icon={<FileText />} />
            <TaskyStatCard label="用途カテゴリ" value={`${latestReport.usage_categories?.length ?? 0}件`} icon={<Tags />} iconTone="success" />
            <TaskyStatCard label="作成日" value={formatDate(latestReport.created_at)} icon={<CalendarDays />} iconTone="warning" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <SignalList title="用途カテゴリ" items={latestReport.usage_categories ?? []} icon={<Tags />} />
            <SignalList title="頻出表現" items={latestReport.frequent_terms ?? []} icon={<FileText />} />
            <NeededStyleList items={latestReport.needed_style_candidates ?? []} />
            <NgImprovementList items={latestReport.improvement_candidates ?? []} />
          </div>

          <section className="rounded-tasky-md border border-warm-border bg-white p-4">
            <div className="mb-3 text-sm font-semibold text-dark">マスク済みサンプル</div>
            {latestReport.safe_prompt_samples && latestReport.safe_prompt_samples.length > 0 ? (
              <div className="space-y-2">
                {latestReport.safe_prompt_samples.map((sample, index) => (
                  <p key={`${index}-${sample.source_table}-${sample.source_id}`} className="rounded-tasky-sm bg-warm-bg px-3 py-2 text-sm text-gray-600">
                    {sample.prompt}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">サンプルなし</p>
            )}
          </section>

          {reports.length > 1 && (
            <section className="rounded-tasky-md border border-warm-border bg-white p-4">
              <div className="mb-3 text-sm font-semibold text-dark">過去レポート</div>
              <div className="divide-y divide-warm-border">
                {reports.slice(1).map((report) => (
                  <div key={report.id} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm text-dark">
                      {formatDate(report.period_start)} - {formatDate(report.period_end)}
                    </span>
                    <span className="text-xs text-gray-500">{report.source_rows_count}件</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default BannerPromptWeeklyReports;
